/**
 * Cart persistence via sessionStorage
 * Stores cart items so they survive page navigations within the store.
 */

import { getEventTypeAdjustmentMultiplier } from '@/components/event-simulator/utils/pricingAdjustmentUtils';
import { getExtrasHeadcount } from '@/utils/specialGuests';
export interface CartItem {
  /** Adicional de frete somado pelo fornecedor na proposta (igual em todos os itens; ver utils/freightExtra). */
  freightExtra?: number;
  productId: string;
  productName: string;
  category: string;
  supplierId: string;
  supplierSlug?: string;
  supplierName?: string;
  productSlug?: string;
  itemName?: string;
  eventHourTime?: string;
  /** Quando true, a duração contratada é um teto ("até X horas"). */
  eventHourIsMax?: boolean;
  /** Antecedência (HH:MM) com que a equipe chega ao local antes do início, do supplier_menu.hour_arrived. */
  hourArrived?: string | null;
  /** Horas adicionais escolhidas pelo cliente acima da duração padrão do cardápio. */
  extraHours?: number;
  /** Tabela de horas adicionais configurada pelo fornecedor. */
  hourExtensionTiers?: { hours: number; mode: 'percent' | 'fixed'; value: number }[];
  /** True quando o cardápio permite contratar horas adicionais. */
  hourExtensionEnabled?: boolean;
  /** Valor (R$) referente às horas adicionais já embutido em `totalPrice`. */
  hourExtensionAmount?: number;

  price: number;
  pricePerPerson: number;
  totalPrice: number;
  basePrice: number;
  imageUrl: string;
  guests: string;
  cep: string;
  date: string; // ISO string
  specialPricing?: boolean;
  specialPrice?: any;
  yearlyAdjustments?: any;
  seasonalGroups?: any;
  /** Percentuais adicionais por tipo de evento configurados pelo fornecedor */
  eventTypeAdjustments?: any;
  /** Tipo de evento escolhido pelo cliente no simulador (opcional) */
  eventType?: string;
  /** Categorias com preço especial (crianças, staff...) configuradas no cardápio */
  specialPricingTiers?: any;
  /** Quantidade informada pelo cliente para cada categoria especial: tierId -> qtd */
  specialGuests?: Record<string, number>;

  isSelectableMenu?: boolean;
  /** True quando o item é um cardápio fixo (preço único, sem detalhes de evento) */
  isFixedMenu?: boolean;
  selectedItemsCount?: number;
  /** For selectable menus: map of child item IDs to quantities */
  selectedMenuItems?: Record<string, number>;
  /** Active substitutions: originalItem -> { substituteItem, additionalCost, isFreeText } */
  substitutions?: Record<string, { substituteItem: string; additionalCost: number; isFreeText: boolean }>;
  /** Itens incluídos pelo fornecedor na proposta: categoryId -> itens (custo por convidado) */
  additions?: import('@/types/menuAdditions').MenuAdditionsMap;
  /** Category selections for fixed menus with maxChoices: categoryId -> selected item names */
  categorySelections?: Record<string, string[]>;
  /** Proposta: o cliente vê só os itens escolhidos pelo fornecedor (+ adicionais com custo), sem os não escolhidos. */
  showOnlySelected?: boolean;
  /** Selected optional items (upgrades) IDs */
  selectedOptionalItems?: string[];
  /** Custo por convidado dos adicionais escolhidos (somado de novo quando o preço é recalculado). */
  optionalCostPerGuest?: number;
  /** Custo dos adicionais de valor fechado (não multiplica por convidados). */
  optionalFixedCost?: number;
  /** Preço de adicional ajustado só nesta proposta pelo fornecedor (id do adicional -> valor e forma de cobrança). */
  optionalOverrides?: import('@/utils/optionalOverrides').OptionalOverrides;
  /** Unidades escolhidas nas opções por quantidade (ex.: Pizza sem Glúten, 4 pessoas). */
  optionalQuantities?: import('@/utils/optionalOverrides').OptionalQuantities;
  /** True when loaded from a shared quote link — prevents price recalculation */
  fromSharedQuote?: boolean;
  isPickup?: boolean;
  pickupAddress?: string;
  addedAt: number;
}

const CART_KEY = 'supplier_cart_items';

export const getCartItems = (): CartItem[] => {
  try {
    const raw = sessionStorage.getItem(CART_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
};

const dispatchCartUpdated = () => {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new Event('cart-updated'));
    window.dispatchEvent(new Event('cart-filters-updated'));
  } catch {
    // ignore
  }
};

export const addCartItem = (item: CartItem): CartItem[] => {
  const items = getCartItems();
  // Replace if same product already in cart
  const filtered = items.filter(i => i.productId !== item.productId);
  filtered.push(item);
  sessionStorage.setItem(CART_KEY, JSON.stringify(filtered));
  dispatchCartUpdated();
  return filtered;
};

export const removeCartItem = (productId: string): CartItem[] => {
  const items = getCartItems().filter(i => i.productId !== productId);
  sessionStorage.setItem(CART_KEY, JSON.stringify(items));
  dispatchCartUpdated();
  return items;
};

export const clearCart = (): void => {
  sessionStorage.removeItem(CART_KEY);
  // Carrinho limpo/compra concluída: descarta também o rascunho de Infos da Festa.
  try {
    Object.keys(sessionStorage).filter((k) => k.startsWith('ameup_infos_draft:')).forEach((k) => sessionStorage.removeItem(k));
  } catch { /* sem storage */ }
  dispatchCartUpdated();
};

export const getCartTotal = (): number => {
  return getCartItems().reduce((sum, item) => sum + item.totalPrice, 0);
};

export const getCartCount = (): number => {
  return getCartItems().length;
};

const hasRealEventType = (value: unknown): value is string => {
  const v = String(value ?? '').trim().toLowerCase();
  return v !== '' && v !== 'evento';
};

/**
 * Tipo de evento a usar no cálculo do preço de um item. Itens que voltam do banco/checkout não trazem `eventType`;
 * sem ele o acréscimo por tipo de evento (ex.: Corporativo +15%) sumia do total ao mudar data/convidados.
 */
export const resolveItemEventType = (item?: { eventType?: string } | null): string | null => {
  if (hasRealEventType(item?.eventType)) return String(item?.eventType).trim();
  try {
    const stored = sessionStorage.getItem('ameup_event_type');
    if (hasRealEventType(stored)) return stored.trim();
  } catch { /* sem sessionStorage */ }
  return null;
};

/**
 * Trava do tipo de evento: quando o tipo vem da proposta montada pelo fornecedor
 * (link com pid/ref, gravado em events.event_type), o cliente NÃO pode trocar —
 * o acréscimo (ex.: Corporativo +15%) faz parte da proposta. A trava só é
 * removida quando o próprio fornecedor troca o tipo na proposta (setSessionEventType).
 */
const EVENT_TYPE_LOCK_KEY = 'ameup_event_type_locked';

/**
 * A trava só vale se foi gravada pela regra atual (marcador v2): antes, TODO pedido feito na loja (todos têm created_by_supplier_id)
 * travava o tipo como se fosse proposta do fornecedor, e a trava antiga ficava presa na sessão do navegador do cliente.
 */
const EVENT_TYPE_LOCK_V2_KEY = 'ameup_event_type_lock_v2';

export const isEventTypeLocked = (): boolean => {
  try { return sessionStorage.getItem(EVENT_TYPE_LOCK_KEY) === 'true' && sessionStorage.getItem(EVENT_TYPE_LOCK_V2_KEY) === 'true'; } catch { return false; }
};

/** Garante o tipo de evento na sessão: se faltar, busca o que está gravado no pedido (events.event_type). */
export const ensureSessionEventType = async (parentEventId?: string | null): Promise<string | null> => {
  const current = resolveItemEventType(null);
  if (!parentEventId) return current;
  // Já travado ou no modo proposta do fornecedor (ele mesmo escolhe o tipo): nada a conferir.
  let supplierQuoteMode = false;
  try { supplierQuoteMode = sessionStorage.getItem('ameup_quote_mode') === 'true'; } catch { /* sem sessionStorage */ }
  if (supplierQuoteMode || (current && isEventTypeLocked())) return current;
  // A conferência no servidor é feita uma vez por pedido nesta sessão (os recálculos do carrinho chamam isto toda hora).
  const checkedKey = `ameup_event_type_checked:${parentEventId}`;
  try {
    if (current && sessionStorage.getItem(checkedKey) === 'true') return current;
  } catch { /* sem sessionStorage */ }
  try {
    const { supabase } = await import('@/integrations/supabase/client');
    // Visitante sem login não lê `events` (RLS): usa a função autorizada da proposta compartilhada.
    let fromDb: unknown = null;
    // Proposta do fornecedor = pedido compartilhado (events.share = true). created_by_supplier_id NÃO serve: todo pedido da loja tem.
    let isSupplierProposal = false;
    let shareKnown = false;
    try {
      const { data: resolved } = await supabase.functions.invoke('resolve-shared-quote', { body: { pid: parentEventId } });
      const parent = resolved?.data?.parentEvent;
      fromDb = parent?.event_type ?? parent?.eventType ?? null;
      if (typeof parent?.share === 'boolean') { isSupplierProposal = parent.share; shareKnown = true; }
    } catch { /* tenta leitura direta */ }
    if (!hasRealEventType(fromDb)) {
      const { data } = await (supabase as any).from('events').select('event_type, share').eq('id', parentEventId).maybeSingle();
      fromDb = data?.event_type;
      if (!shareKnown && typeof data?.share === 'boolean') isSupplierProposal = data.share;
    }
    if (hasRealEventType(fromDb)) {
      // Sem tipo na sessão: assume o da proposta e trava (comportamento original). Com tipo na sessão, só trava se a
      // proposta é do fornecedor: no pedido que o próprio cliente monta, ele continua livre para trocar.
      if (!current || isSupplierProposal) {
        const wasLocked = isEventTypeLocked();
        try {
          sessionStorage.setItem('ameup_event_type', String(fromDb).trim());
          // Sem tipo na sessão e pedido comum (não é proposta): usa o tipo gravado, mas o cliente continua livre para trocar.
          if (isSupplierProposal) {
            sessionStorage.setItem(EVENT_TYPE_LOCK_KEY, 'true');
            sessionStorage.setItem(EVENT_TYPE_LOCK_V2_KEY, 'true');
          }
        } catch { /* sem sessionStorage */ }
        // Avisa a loja/cartão para travarem o campo e recalcularem com o tipo da proposta.
        if (!wasLocked || (current && current !== String(fromDb).trim())) {
          window.dispatchEvent(new CustomEvent<string>(EVENT_TYPE_CHANGED, { detail: String(fromDb).trim() }));
        }
        return String(fromDb).trim();
      }
    }
    try { sessionStorage.setItem(checkedKey, 'true'); } catch { /* sem sessionStorage */ }
  } catch { /* segue sem tipo */ }
  return current;
};

/** Notifica a página que o tipo de evento mudou (cartão, carrinho e loja recalculam na hora). */
export const EVENT_TYPE_CHANGED = 'ameup-event-type-changed';

/** Grava o tipo de evento na sessão e nos itens do carrinho, e avisa a página. */
export const setSessionEventType = (value: string | null | undefined, opts?: { keepLock?: boolean }): void => {
  const next = String(value ?? '').trim();
  try {
    if (next) sessionStorage.setItem('ameup_event_type', next);
    else sessionStorage.removeItem('ameup_event_type');
    // Troca manual (fornecedor ajustando a proposta) destrava o campo;
    // keepLock preserva a trava quando o valor veio da proposta (ensureSessionEventType).
    if (!opts?.keepLock) {
      sessionStorage.removeItem(EVENT_TYPE_LOCK_KEY);
      sessionStorage.removeItem(EVENT_TYPE_LOCK_V2_KEY);
    }
    const items = getCartItems();
    if (items.length > 0) {
      sessionStorage.setItem(CART_KEY, JSON.stringify(items.map((i) => ({ ...i, eventType: next || undefined }))));
    }
  } catch { /* sem sessionStorage */ }
  if (typeof window !== 'undefined') {
    try { window.dispatchEvent(new CustomEvent(EVENT_TYPE_CHANGED, { detail: next })); } catch { /* ignore */ }
  }
};

/** Multiplicador do tipo de evento do item (ex.: Corporativo +15% → 1.15). Vale para todos os itens do cardápio. */
export const getItemEventTypeMultiplier = (
  item: { eventTypeAdjustments?: any; eventType?: string } | null | undefined,
  eventType?: string | null,
): number => {
  const type = eventType ?? resolveItemEventType(item ?? null);
  let adjustments = item?.eventTypeAdjustments;
  if (typeof adjustments === 'string') {
    try { adjustments = JSON.parse(adjustments); } catch { adjustments = []; }
  }
  return getEventTypeAdjustmentMultiplier(Array.isArray(adjustments) ? adjustments : [], type);
};

/**
 * Quanto os adicionais escolhidos somam ao item para N convidados (por pessoa × pessoas + valor fechado). As pessoas são os
 * convidados + as categorias especiais do item (crianças, staff...): 20 convidados + 3 staff = 23 pessoas.
 * O acréscimo por tipo de evento também vale para os adicionais (ex.: garçom em evento Corporativo).
 */
export const getCartItemOptionalCost = (
  item: { optionalCostPerGuest?: number; optionalFixedCost?: number; eventTypeAdjustments?: any; eventType?: string; specialGuests?: unknown },
  guestCount: number,
  eventType?: string | null,
): number => {
  const raw = (Number(item.optionalCostPerGuest) || 0) * getExtrasHeadcount(guestCount, item.specialGuests) + (Number(item.optionalFixedCost) || 0);
  if (raw === 0) return 0;
  return Math.round(raw * getItemEventTypeMultiplier(item, eventType) * 100) / 100;
};

/**
 * Itens do carrinho que vieram de rascunho/proposta/banco trazem os IDs dos adicionais escolhidos mas não o custo
 * deles; sem o custo, qualquer recálculo (mudar convidados, data ou CEP no cabeçalho) tirava os adicionais do total.
 * Busca o preço dos adicionais e devolve os itens completados (ou null se não havia o que completar).
 */
export const enrichCartItemsWithOptionalCosts = async (items: CartItem[]): Promise<CartItem[] | null> => {
  const needs = items.filter(
    (i) => Array.isArray(i.selectedOptionalItems) && i.selectedOptionalItems.length > 0 &&
      i.optionalCostPerGuest === undefined && i.optionalFixedCost === undefined,
  );
  if (needs.length === 0) return null;

  const ids = Array.from(new Set(needs.flatMap((i) => i.selectedOptionalItems as string[])));
  try {
    const { supabase } = await import('@/integrations/supabase/client');
    const { data } = await (supabase as any)
      .from('supplier_menu')
      .select('id, unit_cost, fixed_price, optional_price_mode, optional_min_qty, optional_max_qty')
      .in('id', ids);
    const { splitOptionalCostsWithOverrides } = await import('@/utils/optionalOverrides');
    return items.map((item) => {
      if (!needs.includes(item)) return item;
      // Preço ajustado na proposta (se houver) vale sobre o cadastro do cardápio.
      const { perGuest, fixed } = splitOptionalCostsWithOverrides(
        (data || []) as any[],
        item.selectedOptionalItems as string[],
        item.optionalOverrides,
        item.optionalQuantities,
        getExtrasHeadcount(parseInt(String(item.guests), 10) || 0, item.specialGuests),
      );
      return { ...item, optionalCostPerGuest: perGuest, optionalFixedCost: fixed };
    });
  } catch {
    return null;
  }
};

